import React, { useMemo, useState } from 'react';
import { Users, Sparkles, GitBranch, Wrench, Loader2, AlertCircle, Briefcase, MapPin, Shield, CheckCircle, XCircle, MessageSquare, Calendar, Activity, FolderKanban, MessagesSquare, Palette, Contrast, Type } from 'lucide-react';
import { useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import AdminLayout from './AdminLayout';
import { useAuth } from '@/context/AuthContext';
import { isSystemAdminRole } from '@/lib/roles';
import { isFeatureEnabled } from '@/config/featureFlags';
import { useResolvedFeatureFlags } from '@/hooks/useResolvedFeatureFlags';
import {
  COLOR_THEMES,
  DISPLAY_MODES,
  TEXT_SIZES,
} from '../../../../shared/platform-settings';

interface DashboardStats {
  stats: {
    totalUsers: number;
    activeUsers: number;
    totalTrips: number;
    totalClients: number;
  };
  userMetrics: {
    dau: number;
    wau: number;
    mau: number;
  };
  usageStats: {
    timeRange: string;
    projects: number;
    projectThreads: number;
    standaloneThreads: number;
    userMessages: number;
    assistantMessages: number;
  };
  recentActivity: unknown[];
  systemHealth: {
    status: string;
    uptime: number;
    memory: NodeJS.MemoryUsage;
    timestamp: string;
  };
}

// AR-393 — member counts per platform setting. `system` is its own display-mode
// bucket; it is a stored choice, not a resolved appearance.
interface SettingCount {
  value: string;
  count: number;
}

interface PlatformSettingsUsage {
  totalMembers: number;
  colorTheme: SettingCount[];
  displayMode: SettingCount[];
  textSize: SettingCount[];
}

interface McpToolsSummary {
  total: number;
  enabled: number;
  disabled: number;
  systemCritical: number;
}

type TimeRange = '24h' | '7d' | '30d' | '90d' | '12m';

const TIME_RANGE_LABELS: Record<TimeRange, string> = {
  '24h': 'Last 24 hours',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  '12m': 'Last 12 months',
};

const AdminOverview = () => {
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const isSystemAdmin = isSystemAdminRole(user?.role);
  const [timeRange, setTimeRange] = useState<TimeRange>('7d');

  // Fetch dashboard metrics with time range
  const { data: dashboard, isLoading, error } = useQuery<DashboardStats>({
    queryKey: [`/api/admin/dashboard?timeRange=${timeRange}`],
    staleTime: 30000, // 30 seconds
  });

  // Fetch MCP tools summary (SYSTEM_ADMIN only)
  const { data: mcpSummary, isLoading: mcpLoading } = useQuery<McpToolsSummary>({
    queryKey: ['/api/mcp-admin/tools/summary'],
    staleTime: 30000,
    enabled: isSystemAdmin, // Only fetch for SYSTEM_ADMIN
    retry: false, // Don't retry on 403
  });

  // Platform settings usage (AR-393) — behind the Settings module's migration flag
  const { isEnabled: isResolvedEnabled } = useResolvedFeatureFlags();
  const platformSettingsEnabled =
    isResolvedEnabled('proMigration') && isResolvedEnabled('proMigrationSettings');
  const { data: platformSettings, isLoading: platformSettingsLoading } =
    useQuery<PlatformSettingsUsage>({
      queryKey: ['/api/admin/platform-settings'],
      staleTime: 30000,
      enabled: platformSettingsEnabled,
      retry: false,
    });

  // Check if advanced admin features should be visible
  const advancedFlagEnabled = isFeatureEnabled('agenticAdminAdvanced');

  // Filter action items based on role and feature flags
  const actionItems = useMemo(() => {
    const allItems = [
      {
        title: 'User Management',
        description: 'Assign roles, manage advanced Threads access, and control user status.',
        icon: Users,
        path: '/admin/users',
        actionLabel: 'Manage users',
      },
      {
        title: 'Skills Management',
        description: 'Toggle pre-built AI skills per project or tenant.',
        icon: Sparkles,
        path: '/admin/skills',
        actionLabel: 'Manage skills',
      },
      {
        title: 'Agentic Orchestration',
        description: 'Inspect rollout status, overrides, and audit logs for agentic features.',
        icon: GitBranch,
        path: '/admin/orchestration',
        actionLabel: 'View rollout',
        advancedOnly: true, // Requires SYSTEM_ADMIN + agenticAdminAdvanced flag
      },
      {
        title: 'MCP Tool Management',
        description: 'View and configure MCP tool settings and status (SYSTEM_ADMIN only).',
        icon: Wrench,
        path: '/admin/mcp-tools',
        actionLabel: 'Manage tools',
      },
    ];

    return allItems.filter((item) => {
      // Advanced-only items require SYSTEM_ADMIN + feature flag
      if (item.advancedOnly) {
        return isSystemAdmin && advancedFlagEnabled;
      }
      return true;
    });
  }, [isSystemAdmin, advancedFlagEnabled]);

  const stats = dashboard?.stats;
  const userMetrics = dashboard?.userMetrics;
  const usageStats = dashboard?.usageStats;

  return (
    <AdminLayout
      title="Overview"
      description="Platform metrics and quick access to administrative tools."
    >
      {/* Users Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Users
          </h3>
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/users')}>
            Manage →
          </Button>
        </div>
        {isLoading ? (
          <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Loading metrics...</span>
          </div>
        ) : error ? (
          <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950 rounded-lg px-4 py-3">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm">Metrics temporarily unavailable</span>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <MetricCard
              label="Total Users"
              value={stats?.totalUsers ?? 0}
              icon={Users}
            />
            <MetricCard
              label="MAU"
              value={userMetrics?.mau ?? 0}
              icon={Activity}
              tooltip="Monthly Active Users (last 30 days)"
            />
            <MetricCard
              label="WAU"
              value={userMetrics?.wau ?? 0}
              icon={Activity}
              tooltip="Weekly Active Users (last 7 days)"
            />
            <MetricCard
              label="DAU"
              value={userMetrics?.dau ?? 0}
              icon={Activity}
              highlight
              tooltip="Daily Active Users (last 24 hours)"
            />
          </div>
        )}
      </div>

      {/* Usage Stats Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Usage Stats
          </h3>
          <Select value={timeRange} onValueChange={(v) => setTimeRange(v as TimeRange)}>
            <SelectTrigger className="w-[160px] h-8 text-sm">
              <Calendar className="h-3.5 w-3.5 mr-2 text-slate-400" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(TIME_RANGE_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {isLoading ? (
          <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Loading usage stats...</span>
          </div>
        ) : error ? (
          <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950 rounded-lg px-4 py-3">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm">Usage stats temporarily unavailable</span>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <MetricCard
              label="Projects"
              value={usageStats?.projects ?? 0}
              icon={FolderKanban}
              highlight
              tooltip="Projects created in this period"
            />
            <MetricCard
              label="Project Threads"
              value={usageStats?.projectThreads ?? 0}
              icon={MessagesSquare}
              tooltip="Threads created inside projects"
            />
            <MetricCard
              label="Standalone Threads"
              value={usageStats?.standaloneThreads ?? 0}
              icon={MessageSquare}
              tooltip="Threads created outside of projects"
            />
            <MetricCard
              label="User Messages"
              value={usageStats?.userMessages ?? 0}
              icon={Users}
              tooltip="Messages sent by users"
            />
            <MetricCard
              label="AI Responses"
              value={usageStats?.assistantMessages ?? 0}
              icon={Sparkles}
              tooltip="Messages from AI assistant"
            />
          </div>
        )}
      </div>

      {/* Legacy Metrics (Trips & Clients) - COMMENTED OUT FOR LATER
      <div className="mb-8">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-4">
          Platform Data
        </h3>
        {isLoading ? (
          <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Loading...</span>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <MetricCard
              label="Total Trips"
              value={stats?.totalTrips ?? 0}
              icon={MapPin}
            />
            <MetricCard
              label="Total Clients"
              value={stats?.totalClients ?? 0}
              icon={Briefcase}
            />
          </div>
        )}
      </div>
      */}

      {/* Platform Settings (AR-393) */}
      {platformSettingsEnabled && (
        <div className="mb-8">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-4">
            Platform Settings
          </h3>
          {platformSettingsLoading ? (
            <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Loading platform settings...</span>
            </div>
          ) : platformSettings ? (
            <div className="grid gap-4 lg:grid-cols-3">
              <SettingBreakdown
                title="Color Theme"
                icon={Palette}
                rows={platformSettings.colorTheme}
                labels={COLOR_THEME_LABELS}
                total={platformSettings.totalMembers}
              />
              <SettingBreakdown
                title="Display Mode"
                icon={Contrast}
                rows={platformSettings.displayMode}
                labels={DISPLAY_MODE_LABELS}
                total={platformSettings.totalMembers}
              />
              <SettingBreakdown
                title="Text Size"
                icon={Type}
                rows={platformSettings.textSize}
                labels={TEXT_SIZE_LABELS}
                total={platformSettings.totalMembers}
              />
            </div>
          ) : (
            <div className="text-sm text-slate-500 dark:text-slate-400">
              Platform settings usage unavailable
            </div>
          )}
        </div>
      )}

      {/* MCP Tools Summary (SYSTEM_ADMIN only) */}
      {isSystemAdmin && (
        <div className="mb-8">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-4">
            MCP Tools
          </h3>
          {mcpLoading ? (
            <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Loading MCP summary...</span>
            </div>
          ) : mcpSummary ? (
            <Card 
              className="cursor-pointer hover:border-orange-300 transition-colors"
              onClick={() => navigate('/admin/mcp-tools')}
            >
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Wrench className="h-5 w-5 text-orange-600" />
                    <CardTitle className="text-lg">Tool Configuration</CardTitle>
                  </div>
                  <Button variant="ghost" size="sm">
                    Manage →
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-semibold text-slate-900 dark:text-slate-100">{mcpSummary.total}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Total</p>
                  </div>
                  <div>
                    <p className="text-2xl font-semibold text-green-600 dark:text-green-400">{mcpSummary.enabled}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center justify-center gap-1">
                      <CheckCircle className="h-3 w-3" /> Enabled
                    </p>
                  </div>
                  <div>
                    <p className="text-2xl font-semibold text-slate-400 dark:text-slate-500">{mcpSummary.disabled}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center justify-center gap-1">
                      <XCircle className="h-3 w-3" /> Disabled
                    </p>
                  </div>
                  <div>
                    <p className="text-2xl font-semibold text-red-600 dark:text-red-400">{mcpSummary.systemCritical}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center justify-center gap-1">
                      <Shield className="h-3 w-3" /> Critical
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="text-sm text-slate-500 dark:text-slate-400">
              MCP summary unavailable
            </div>
          )}
        </div>
      )}

      {/* Action Cards Section - COMMENTED OUT FOR LATER
      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-4">
          Admin Tools
        </h3>
        <div className="grid gap-6 md:grid-cols-2">
          {actionItems.map((item) => {
            const Icon = item.icon;
            return (
              <Card key={item.title} className="flex h-full flex-col justify-between">
                <div>
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-50 dark:bg-orange-900/50 text-orange-600 dark:text-orange-400">
                        <Icon className="h-5 w-5" aria-hidden="true" />
                      </span>
                      <div>
                        <CardTitle className="text-xl">{item.title}</CardTitle>
                        <CardDescription>{item.description}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                </div>
                <CardContent className="pt-0">
                  <Button
                    type="button"
                    onClick={() => navigate(item.path)}
                  >
                    {item.actionLabel}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
      */}
    </AdminLayout>
  );
};

const COLOR_THEME_LABELS: Record<string, string> = Object.fromEntries(
  COLOR_THEMES.map((t) => [t.id, t.label]),
);
const DISPLAY_MODE_LABELS: Record<string, string> = Object.fromEntries(
  DISPLAY_MODES.map((m) => [m.id, m.label]),
);
const TEXT_SIZE_LABELS: Record<string, string> = Object.fromEntries(
  TEXT_SIZES.map((t) => [t.id, t.label]),
);

interface SettingBreakdownProps {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  rows: SettingCount[];
  labels: Record<string, string>;
  total: number;
}

const SettingBreakdown = ({ title, icon: Icon, rows, labels, total }: SettingBreakdownProps) => (
  <Card>
    <CardHeader className="pb-2">
      <div className="flex items-center gap-2">
        <Icon className="h-5 w-5 text-orange-600" />
        <CardTitle className="text-lg">{title}</CardTitle>
      </div>
      <CardDescription>{total.toLocaleString()} members</CardDescription>
    </CardHeader>
    <CardContent>
      <ul className="space-y-2">
        {rows.map((row) => (
          <li key={row.value} className="flex items-center justify-between text-sm">
            <span className="text-slate-600 dark:text-slate-300">
              {labels[row.value] ?? row.value}
            </span>
            <span className="font-semibold text-slate-900 dark:text-slate-100">
              {row.count.toLocaleString()}
            </span>
          </li>
        ))}
      </ul>
    </CardContent>
  </Card>
);

interface MetricCardProps {
  label: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
  highlight?: boolean;
  tooltip?: string;
}

const MetricCard = ({ label, value, icon: Icon, highlight, tooltip }: MetricCardProps) => (
  <div 
    className={`rounded-lg border p-4 ${highlight ? 'bg-orange-50 dark:bg-orange-900/30 border-orange-200 dark:border-orange-800' : 'bg-white dark:bg-gray-800 border-slate-200 dark:border-gray-700'}`}
    title={tooltip}
  >
    <div className="flex items-center gap-3">
      <span className={`flex h-8 w-8 items-center justify-center rounded-full ${highlight ? 'bg-orange-100 dark:bg-orange-800/50 text-orange-600 dark:text-orange-400' : 'bg-slate-100 dark:bg-gray-700 text-slate-500 dark:text-slate-400'}`}>
        <Icon className="h-4 w-4" />
      </span>
      <div>
        <p className="text-2xl font-semibold text-slate-900 dark:text-slate-100">{value.toLocaleString()}</p>
        <p className={`text-xs ${highlight ? 'text-orange-600 dark:text-orange-400' : 'text-slate-500 dark:text-slate-400'}`}>{label}</p>
      </div>
    </div>
  </div>
);

export default AdminOverview;
